import { ExperimentStatus } from '@prismancy/shared'

export const EXPERIMENT_CHANGE_TYPES = [
  'attribute',
  'hide',
  'html',
  'link',
  'move',
  'style',
  'text',
] as const

export type ExperimentChangeType = typeof EXPERIMENT_CHANGE_TYPES[number]

const segment = (value: string): string => encodeURIComponent(value)

export const normalizeAppPathname = (pathname: string): string => {
  if (pathname === '/') return pathname
  return pathname.replace(/\/+$/, '')
}

export const isExperimentStatus = (value: string | null | undefined): value is ExperimentStatus => (
  Boolean(value) && Object.values(ExperimentStatus).includes(value as ExperimentStatus)
)

export const resolveExperimentStatus = (
  value: string | null | undefined,
): ExperimentStatus => isExperimentStatus(value) ? value : ExperimentStatus.DRAFT

export const appRoutePatterns = {
  experiments: {
    status: ':status',
    setup: ':experimentId/setup',
    targeting: ':experimentId/targeting',
    review: ':experimentId/review',
    reporting: ':experimentId/reporting',
    variant: ':experimentId/:variantId',
    change: ':experimentId/:variantId/:changeType',
    existingChange: ':experimentId/:variantId/:changeType/:changeId',
  },
} as const

export const appRoutes = {
  root: '/',
  login: '/login',
  requestPasswordReset: '/login/request-password-reset',
  workspace: {
    noWorkspace: '/workspace/no-workspace-found',
    join: '/workspace/join-workspace',
    pendingInvites: (email: string): string => `/authenticated-verification/pending-invites/${segment(email)}`,
    create: '/create-workspace',
  },
  experiments: {
    root: '/experiments',
    status: (status: ExperimentStatus): string => `/experiments/${status}`,
    setup: (experimentId: string): string => `/experiments/${segment(experimentId)}/setup`,
    targeting: (experimentId: string): string => `/experiments/${segment(experimentId)}/targeting`,
    review: (experimentId: string): string => `/experiments/${segment(experimentId)}/review`,
    reporting: (experimentId: string): string => `/experiments/${segment(experimentId)}/reporting`,
    variant: (experimentId: string, variantId: string): string => (
      `/experiments/${segment(experimentId)}/${segment(variantId)}`
    ),
    change: (
      experimentId: string,
      variantId: string,
      changeType: ExperimentChangeType,
      changeId?: string,
    ): string => {
      const base = `/experiments/${segment(experimentId)}/${segment(variantId)}/${changeType}`
      return changeId ? `${base}/${segment(changeId)}` : base
    },
  },
  billing: {
    root: '/billing',
    success: '/billing/success',
    failed: '/billing/failed',
  },
  profile: '/profile',
  settings: {
    root: '/settings',
    anchorLinks: '/settings/anchor-links',
    team: '/settings/team',
    activity: '/settings/activity',
  },
} as const
